"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createSupplierAction,
  updateSupplierAction,
  deleteSupplierAction,
} from "@/actions/ops-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Search,
  Mail,
  Phone,
  Plus,
  Trash2,
  Edit2,
  Star,
  Building,
  TrendingUp,
} from "lucide-react";

interface SupplierItem {
  id: string;
  name: string;
  category: string;
  contactEmail: string;
  contactPhone: string;
  rating: number;
  notes: string;
  status: "active" | "inactive";
  createdAt: string;
  updatedAt: string;
}

interface SuppliersClientProps {
  initialSuppliers: SupplierItem[];
}

const CATEGORIES = [
  "Hardware",
  "Software",
  "Consulenza",
  "Servizi",
  "Logistica",
  "Altro",
];

export function SuppliersClient({ initialSuppliers }: SuppliersClientProps) {
  const router = useRouter();
  const [suppliersList, setSuppliersList] =
    useState<SupplierItem[]>(initialSuppliers);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierItem | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form State
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Software");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [rating, setRating] = useState(5);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<"active" | "inactive">("active");

  useEffect(() => {
    setSuppliersList(initialSuppliers);
  }, [initialSuppliers]);

  const handleOpenAddModal = () => {
    setEditingSupplier(null);
    setName("");
    setCategory("Software");
    setContactEmail("");
    setContactPhone("");
    setRating(5);
    setNotes("");
    setStatus("active");
    setError("");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (supplier: SupplierItem) => {
    setEditingSupplier(supplier);
    setName(supplier.name);
    setCategory(supplier.category);
    setContactEmail(supplier.contactEmail);
    setContactPhone(supplier.contactPhone);
    setRating(supplier.rating);
    setNotes(supplier.notes);
    setStatus(supplier.status);
    setError("");
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Il nome del fornitore è obbligatorio.");
      return;
    }
    if (!contactEmail.trim() || !contactEmail.includes("@")) {
      setError("Inserire un indirizzo email valido.");
      return;
    }

    setLoading(true);

    try {
      if (editingSupplier) {
        // Edit flow
        const res = await updateSupplierAction({
          id: editingSupplier.id,
          name,
          category,
          contactEmail,
          contactPhone,
          rating,
          notes,
          status,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsModalOpen(false);
          router.refresh();
        }
      } else {
        // Create flow
        const res = await createSupplierAction({
          name,
          category,
          contactEmail,
          contactPhone,
          rating,
          notes,
          status,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsModalOpen(false);
          router.refresh();
        }
      }
    } catch (err: any) {
      setError(
        err.message || "Si è verificato un errore durante il salvataggio.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo fornitore?")) return;

    try {
      const res = await deleteSupplierAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err) {
      alert("Errore durante l'eliminazione.");
    }
  };

  const filteredSuppliers = suppliersList.filter((sup) => {
    const matchesSearch =
      sup.name.toLowerCase().includes(search.toLowerCase()) ||
      sup.contactEmail.toLowerCase().includes(search.toLowerCase()) ||
      sup.category.toLowerCase().includes(search.toLowerCase());

    const matchesCategory =
      categoryFilter === "all" || sup.category === categoryFilter;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Search & Filter bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca fornitori per nome, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex w-full sm:w-auto gap-3 items-center justify-between sm:justify-end">
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-[170px]"
          >
            <option value="all">Tutte le categorie</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </Select>

          <Button onClick={handleOpenAddModal} className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" />
            Nuovo Fornitore
          </Button>
        </div>
      </div>

      {/* Grid List */}
      {filteredSuppliers.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
          <Building className="h-10 w-10 text-primary-500 mb-3" />
          <h3 className="text-base font-bold text-text-primary">
            Nessun fornitore trovato
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Non ci sono fornitori corrispondenti ai filtri selezionati. Crea un
            nuovo fornitore per cominciare.
          </p>
          <Button onClick={handleOpenAddModal} className="mt-4 gap-1.5">
            <Plus className="h-4 w-4" />
            Aggiungi Primo Fornitore
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSuppliers.map((supplier) => (
            <Card
              key={supplier.id}
              className={`relative overflow-hidden flex flex-col justify-between hover:border-primary-500/50 transition-all border-t-4 group cursor-pointer ${
                supplier.status === "active"
                  ? "border-t-success"
                  : "border-t-text-muted"
              }`}
              onClick={() => handleOpenEditModal(supplier)}
            >
              {/* Hover Actions */}
              <div
                className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                onClick={(e) => e.stopPropagation()}
              >
                <Link
                  href={`/explore?nodeId=${supplier.id}`}
                  title="Esplora relazioni"
                >
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-7 w-7 p-0 flex items-center justify-center"
                  >
                    <TrendingUp className="h-3.5 w-3.5 text-text-secondary hover:text-primary-600" />
                  </Button>
                </Link>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => handleOpenEditModal(supplier)}
                  title="Modifica"
                  className="h-7 w-7 p-0 flex items-center justify-center"
                >
                  <Edit2 className="h-3.5 w-3.5 text-text-secondary hover:text-primary-600" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => handleDelete(supplier.id)}
                  title="Elimina"
                  className="h-7 w-7 p-0 flex items-center justify-center hover:bg-error-soft hover:text-error"
                >
                  <Trash2 className="h-3.5 w-3.5 text-text-secondary" />
                </Button>
              </div>

              <CardHeader className="pb-3 pr-24">
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="neutral"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {supplier.category}
                    </Badge>
                    <Badge
                      variant={
                        supplier.status === "active" ? "success" : "neutral"
                      }
                      onClick={(e) => e.stopPropagation()}
                    >
                      {supplier.status === "active" ? "Attivo" : "Inattivo"}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-base text-text-primary leading-snug">
                    <Link
                      href={`/explore?nodeId=${supplier.id}`}
                      className="hover:text-primary-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {supplier.name}
                    </Link>
                  </h3>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pb-4 flex-1">
                {/* Contact info */}
                <div className="space-y-2 text-xs text-text-secondary">
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-text-muted shrink-0" />
                    <span className="truncate">{supplier.contactEmail}</span>
                  </div>
                  {supplier.contactPhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5 text-text-muted shrink-0" />
                      <span>{supplier.contactPhone}</span>
                    </div>
                  )}
                </div>

                {/* Rating (Stars) */}
                <div
                  className="flex items-center gap-1 pt-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 shrink-0 ${
                        i < supplier.rating
                          ? "text-warning fill-warning"
                          : "text-border-strong fill-transparent"
                      }`}
                    />
                  ))}
                </div>

                {/* Notes */}
                {supplier.notes && (
                  <div className="text-xs text-text-secondary bg-card-muted/50 p-2.5 rounded-lg border border-border/40 line-clamp-3">
                    {supplier.notes}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingSupplier
                ? "Modifica Fornitore"
                : "Aggiungi Nuovo Fornitore"}
            </DialogTitle>
            <DialogDescription>
              Inserisci o aggiorna le informazioni del venditore o fornitore di
              servizi.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="sup-name">Nome Fornitore</Label>
              <Input
                id="sup-name"
                placeholder="es. AWS Cloud Services"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="sup-category">Categoria</Label>
                <Select
                  id="sup-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={loading}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="sup-status">Stato Operativo</Label>
                <Select
                  id="sup-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="active">Attivo</option>
                  <option value="inactive">Inattivo</option>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="sup-email">Email di Contatto</Label>
                <Input
                  id="sup-email"
                  type="email"
                  placeholder="es. support@aws.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="sup-phone">
                  Telefono di Contatto (Opzionale)
                </Label>
                <Input
                  id="sup-phone"
                  placeholder="es. +39 02 123456"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sup-rating">Valutazione / Affidabilità</Label>
              <Select
                id="sup-rating"
                value={rating}
                onChange={(e) => setRating(parseInt(e.target.value))}
                disabled={loading}
              >
                <option value="5">5 Stelle - Eccellente</option>
                <option value="4">4 Stelle - Ottimo</option>
                <option value="3">3 Stelle - Buono</option>
                <option value="2">2 Stelle - Sufficiente</option>
                <option value="1">1 Stella - Scarso</option>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sup-notes">
                Note & Dettagli Operativi (Opzionale)
              </Label>
              <textarea
                id="sup-notes"
                placeholder="Inserisci dettagli su contratti, SLA, o note utili..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={loading}
                rows={3}
                className="flex w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground resize-none"
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingSupplier ? "Salva Modifiche" : "Aggiungi Fornitore"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
